import { Component, inject, Input as RouterInput, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { BaseComponent } from '../../shared/base.component';
import { UpdateCustomerState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { CustomerComponent } from '../shared/customer.component';
import { Customer } from '../shared/customer.model';
import { CustomerService } from '../shared/customer.service';

@Component({
    selector: 'app-update-customer',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        SiteHeaderComponent,
        CustomerComponent
    ],
    templateUrl: './update-customer.component.html',
    styleUrl: './update-customer.component.css'
})
export class UpdateCustomerComponent extends BaseComponent {

    @RouterInput() id: number = -1;
    @ViewChild(CustomerComponent) customerComponent!:CustomerComponent;

    private customerService: CustomerService = inject(CustomerService);
    
    private static _retrievingCustomer = "Retrieving Customer";
    private static _retrievingCustomerError = `${UpdateCustomerComponent._retrievingCustomer} error:`;
    private static _updatingCustomer = "Updating Customer";
    private static _updatingCustomerError = `${UpdateCustomerComponent._updatingCustomer} error:`;

    private _customer: Customer = new Customer();

    public ngAfterViewInit() {
        const state: UpdateCustomerState | undefined = this.stateManager.updateCustomerState;

        if (state !== undefined) {
            this._customer = state.customer;
            this.customerComponent.restoreState(state.customerComponentState);
            super.restoreState(state.baseComponentState);
        } else {
            this.customerComponent.init();
            this.loadCustomer(this.id);
        }
    }

    public ngOnDestroy() {
        const state: UpdateCustomerState = {} as UpdateCustomerState;
        state.customer = this._customer;
        state.customerComponentState = this.customerComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.updateCustomerState = state;
    }

    protected async loadCustomer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.customerService.getCustomer(await getIdToken(currentUser), id);
            super.message = "";
            super.showSnackBar(`${UpdateCustomerComponent._retrievingCustomer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-customer"] == "success") {
                        this._customer = Customer.toCustomer(json["customer"]);
                        this.customerComponent.populateFormFromModel(this._customer);
                        super.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateCustomerComponent._retrievingCustomerError} ${(<Error>error).message}`;
                    this.message = message;
                }
                super.dismissSnackBar();
            })
        } else {
            const message = `${UpdateCustomerComponent._retrievingCustomerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected cancel() {
        this.router.navigate(["/view-customers"], { state: { restore: true } });
    }

    protected async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            this._customer = this.customerComponent.createModelFromForm(this._customer);

            const observable = await this.customerService.updateCustomer(await getIdToken(currentUser), this._customer);
            this.message = "";
            super.showSnackBar(`${UpdateCustomerComponent._updatingCustomer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["update-customer"] == "success") {
                        this.stateManager.clearCustomerNames();
                        const queryParam = `Customer '${this._customer.first_name} ${this._customer.last_name}' updated.`;
                        this.router.navigate(["/view-customers"], { queryParams: {"overrideSnackBar": queryParam}, state: { refresh: true } });
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateCustomerComponent._updatingCustomerError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        } else {
            const message = `${UpdateCustomerComponent._updatingCustomerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }
}
